import React, { useEffect, useRef } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface RichTextEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  height?: number
  readOnly?: boolean
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  height = 200,
  readOnly = false,
}) => {
  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ]
  }

  // Quill formats configuration
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link', 'image'
  ]

  const handleChange = (
    _html: string,
    _delta: any,
    source: 'user' | 'api' | 'silent',
    editor: any
  ) => {
    // Track if user is actively typing
    if (source === 'user') {
      isUserTypingRef.current = true
      // Reset the flag after a short delay
      setTimeout(() => {
        isUserTypingRef.current = false
      }, 100)
    }
    
    // Emit Quill's HTML as-is so <p>/<br> are preserved
    onChange?.(editor.getHTML())
  }

  const quillRef = useRef<ReactQuill | null>(null)
  const lastAppliedHtmlRef = useRef<string | undefined>(undefined)
  const isUserTypingRef = useRef(false)


  // Force HTML content to be set correctly when value changes (only on external updates)
  useEffect(() => {
    const quill = quillRef.current?.getEditor?.()
    if (!quill || !value || isUserTypingRef.current) return
    
    // Only update if the value has actually changed and user is not typing
    if (value !== lastAppliedHtmlRef.current) {
      // Clear the editor first
      quill.setContents([])
      
      // Use a small delay to ensure the clear operation completes
      setTimeout(() => {
        // Set the HTML content directly
        quill.root.innerHTML = value
        lastAppliedHtmlRef.current = value
      }, 10)
    }
  }, [value])

  return (
    <div 
      className="rich-text-editor"
      style={{
        marginBottom: '20px'
      }}
    >
      {/* Ensure the editor surface has a minimum height */}
      <style>{`.rich-text-editor .ql-editor { min-height: ${height}px; }`}</style>
      <ReactQuill
        ref={quillRef as any}
        theme="snow"
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{
          minHeight: `${height + 42}px` // Include toolbar height
        }}
      />
    </div>
  )
}

export default RichTextEditor